"use client";

import { TableHeadWithFontBold } from "@/components/Table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import dayjs from "@/lib/dayjs";
import { cn } from "@/lib/utils";
import { pagesPath } from "@/utils/pages-path";
import NotificationIcon from "@public/icons/notification-icon.svg";
import { useRouter } from "next/navigation";
import { type FC, useState } from "react";
import { NOTIFICATION_CATEGORIES } from "./constants";
import { useInAppNotifications } from "./useInAppNotifications";

const UnreadInAppNotificationBadge: FC = () => (
  <div className="absolute -top-1 -right-1 h-3 w-3 rounded-full border border-white bg-error" />
);

export const InAppNotificationPopover: FC<{
  officeId: string;
}> = ({ officeId }) => {
  const router = useRouter();
  const [isOpen, setIsOpen] = useState(false);
  const {
    hasUnread,
    notifications,
    isLoading,
    lastCheckedAt,
    markAsReadAndUpdateLastChecked,
    refreshLastChecked,
  } = useInAppNotifications({
    officeId,
  });

  const isNewNotification = (scheduledAt: string) => {
    if (!lastCheckedAt) return true; // 最終確認時刻がない場合は新しい通知として扱う
    return new Date(scheduledAt) > new Date(lastCheckedAt);
  };

  const handleRowClick = (notification: (typeof notifications)[number]) => {
    setIsOpen(false); // Popoverを閉じる
    router.push(
      pagesPath.employers
        ._officeId(officeId)
        .in_app_notifications._id(notification.id)
        .$url().path,
    );
  };

  const handleNotificationListClick = () => {
    setIsOpen(false); // Popoverを閉じる
    router.push(
      pagesPath.employers._officeId(officeId).in_app_notifications.$url().path,
    );
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open); // 状態を同期
    if (open) {
      // 通知ベルを既読に切り替え、最終確認時刻を更新
      markAsReadAndUpdateLastChecked();
    } else {
      // ポップオーバーを閉じる時に最終確認時刻のキャッシュを更新
      refreshLastChecked();
    }
  };

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          className={cn(
            "flex items-center gap-2 px-3 py-2 font-bold text-primary-7 hover:bg-primary-0.5 hover:text-primary-7",
            !hasUnread && "font-normal",
          )}
        >
          <span className="relative">
            <NotificationIcon className="fill-primary-7" />
            {hasUnread && <UnreadInAppNotificationBadge />}
          </span>
          <span className="hidden sm:inline">通知</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[calc(100vw-3rem)] border border-monotone-3 bg-white p-0 shadow-lg sm:w-[620px] lg:w-[720px]"
        align="end"
      >
        {/* ヘッダー */}
        <div className="flex items-center justify-end px-4 py-3">
          <Button
            variant="secondary"
            className="h-8 px-3 py-2 text-sm"
            onClick={handleNotificationListClick}
          >
            通知一覧
          </Button>
        </div>

        {/* テーブル */}
        <div className="mx-4 mb-4">
          <div className="overflow-x-auto rounded-lg">
            <div className="max-h-[380px] overflow-y-auto">
              <Table>
                <TableHeader className="bg-monotone-1 whitespace-nowrap md:whitespace-normal">
                  <TableRow className="h-14">
                    <TableHeadWithFontBold className="w-36 text-sm">
                      カテゴリ
                    </TableHeadWithFontBold>
                    <TableHeadWithFontBold className="text-sm">
                      内容
                    </TableHeadWithFontBold>
                    <TableHeadWithFontBold className="w-36 text-sm">
                      通知日
                    </TableHeadWithFontBold>
                  </TableRow>
                </TableHeader>
                <TableBody className="whitespace-nowrap md:whitespace-normal">
                  {isLoading ? (
                    <TableRow className="h-14">
                      <TableCell
                        colSpan={3}
                        className="h-16 text-center text-sm text-monotone-5"
                      >
                        読み込み中...
                      </TableCell>
                    </TableRow>
                  ) : notifications.length === 0 ? (
                    <TableRow className="h-14">
                      <TableCell
                        colSpan={3}
                        className="h-16 text-center text-sm text-monotone-5"
                      >
                        通知がありません
                      </TableCell>
                    </TableRow>
                  ) : (
                    notifications.map((notification) => (
                      <TableRow
                        key={notification.id}
                        className="h-14 cursor-pointer hover:bg-monotone-0.5"
                        onClick={() => handleRowClick(notification)}
                      >
                        <TableCell className="py-2 text-sm">
                          <Badge className="bg-primary-0.5 px-2 py-0.5 text-sm font-bold text-primary-6">
                            {
                              NOTIFICATION_CATEGORIES[notification.category]
                                .label
                            }
                          </Badge>
                        </TableCell>
                        <TableCell className="py-2 text-sm">
                          <div
                            className={cn(
                              "line-clamp-2",
                              isNewNotification(notification.scheduledAt) &&
                                "font-bold",
                            )}
                          >
                            {notification.title}
                          </div>
                        </TableCell>
                        <TableCell className="py-2 text-sm">
                          <div
                            className={cn(
                              isNewNotification(notification.scheduledAt) &&
                                "font-bold",
                            )}
                          >
                            {dayjs(notification.scheduledAt).format(
                              "YYYY年M月D日",
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
